import React, { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import LoadingIndicator from '../LoadingIndicator';
import UseCaseShowcase from '../UseCaseShowcase';

/**
 * CapabilityShowcase - Apple-style capability sections
 * 
 * Design patterns from Apple:
 * - Full-viewport immersive sections
 * - Horizontal carousels with auto-advance
 * - Massive stats/numbers as visual elements
 * - Large sample imagery (not tiny mockups)
 * - Tabbed feature exploration
 * - Minimal text, maximum visual impact
 */

// ============================================
// SECTION 0: Promise Section (NEW)
// Leads with positive, nests problem subtly
// ============================================

const PromiseSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const scrollRef = useRef(null);

  const scrollToCard = (index) => {
    setActiveTab(index);
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.children[0]?.offsetWidth || 0;
      scrollRef.current.scrollTo({
        left: index * (cardWidth + 16),
        behavior: 'smooth'
      });
    }
  };
  
  return (
    <section className="bg-[#1a1a18] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="max-w-4xl mx-auto md:text-center">
          {/* Main lead */}
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            Real-world AI Guide
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl max-w-2xl md:mx-auto font-bold text-white leading-[1.1] mb-6">
            Expert guidance,<br />
            whenever you need it.
          </h2>
          
          {/* Benefit statement */}
          <p className="text-base md:text-xl font-medium text-white/60 mb-12 max-w-2xl md:mx-auto">
            AI that understands your situation and guides you through complex tasks, step by step. No more guessing. 
            No more figuring it out alone.
          </p>
        </div>
        {/* Large visual with tabs */}
        <div className="relative">
          {/* Main visual area */}
          <UseCaseShowcase />
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 0.5: Guided Walkthrough Section (NEW)
// Step-by-step action lists with four-panel illustrations
// ============================================

const GuidedWalkthroughSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeUseCaseIndex, setActiveUseCaseIndex] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const useCases = [
    {
      id: 'home-repair',
      label: 'Home Repair',
      image: '/home/walkthrough-home-repair.webp',
      imageAlt: 'Four-panel illustration showing door hinge repair steps',
      steps: [
        { id: 1, label: 'Locate the hinge pin', icon: '🔍' },
        { id: 2, label: 'Apply lubricant (2-3 drops)', icon: '💧' },
        { id: 3, label: 'Work the door back and forth', icon: '🚪' },
        { id: 4, label: 'Wipe excess and test', icon: '✨' },
      ]
    },
    {
      id: 'equipment',
      label: 'Equipment Service',
      image: '/home/walkthrough-equipment.webp',
      imageAlt: 'Four-panel illustration showing water heater diagnosis steps',
      steps: [
        { id: 1, label: 'Locate the control panel', icon: '🎛️' },
        { id: 2, label: 'Inspect the error indicator', icon: '⚠️' },
        { id: 3, label: 'Check the pressure valve', icon: '🔧' },
        { id: 4, label: 'Test and verify fix', icon: '✅' },
      ]
    },
    {
      id: 'nature',
      label: 'Nature Guide',
      image: '/home/walkthrough-nature.webp',
      imageAlt: 'Four-panel illustration showing mushroom identification steps',
      steps: [
        { id: 1, label: 'Capture the specimen', icon: '📸' },
        { id: 2, label: 'Analyze key features', icon: '🔎' },
        { id: 3, label: 'Learn about the species', icon: '📚' },
        { id: 4, label: 'Get foraging advice', icon: '🍄' },
      ]
    }
  ];

  const activeUseCase = useCases[activeUseCaseIndex];
  const steps = activeUseCase.steps;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance steps, then cycle to next use case
  useEffect(() => {
    if (!isVisible || isPaused) return;
    
    const timer = setInterval(() => {
      setActiveStepIndex(prev => {
        const nextStep = prev + 1;
        if (nextStep >= steps.length) {
          setTimeout(() => {
            setActiveUseCaseIndex(prevUC => (prevUC + 1) % useCases.length);
            setActiveStepIndex(0);
          }, 600);
          return prev;
        }
        return nextStep;
      });
    }, 2400);
    
    return () => clearInterval(timer);
  }, [isVisible, isPaused, steps.length, useCases.length]);

  useEffect(() => {
    setActiveStepIndex(0);
  }, [activeUseCaseIndex]);

  const handleSelectUseCase = (index) => {
    setActiveUseCaseIndex(index);
    setActiveStepIndex(0);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 12000);
  };

  return (
    <section ref={sectionRef} className="bg-black py-16 lg:py-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Section header */}
        <div 
          className="text-left md:text-center mb-10 lg:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            Step-by-Step Guidance
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4">
            Clear actions,<br />every step of the way.
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            Complex tasks broken into simple, actionable steps. Follow along at your own pace—Okio adapts instructions to your exact situation.
          </p>
        </div>

        {/* Use Case Selector */}
        <div 
          className="flex justify-center gap-2 mb-8 lg:mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.8s ease-out 0.2s'
          }}
        >
          {useCases.map((uc, index) => (
            <button
              key={uc.id}
              onClick={() => handleSelectUseCase(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                index === activeUseCaseIndex
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
              }`}
            >
              {uc.label}
            </button>
          ))}
        </div>

        {/* Main content: Four-panel image + Steps list */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
          
          {/* First: Four-panel illustration */}
          <div 
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(30px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.4s'
            }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 lg:scale-[1.03] transform-gpu">
              <img 
                src={activeUseCase.image}
                alt={activeUseCase.imageAlt}
                className="w-full aspect-[16/9] object-cover transition-opacity duration-500"
              />
              
              {/* Step highlight overlay - subtle glow on active quadrant */}
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none">
                {[0, 1, 2, 3].map((quadrant) => (
                  <div 
                    key={quadrant}
                    className={`transition-all duration-500 ${
                      quadrant === activeStepIndex
                        ? 'bg-emerald-500/10 ring-2 ring-inset ring-emerald-500/40'
                        : quadrant < activeStepIndex
                          ? 'bg-white/5'
                          : ''
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Second: Step card */}
          <div 
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'all 0.8s ease-out 0.3s'
            }}
          >
            <div className="bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl p-5 lg:p-6 border border-white/10">
              {/* Progress bar */}
              <div className="flex gap-1.5 mb-4">
                {steps.map((_, i) => (
                  <div 
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                      i <= activeStepIndex ? 'bg-emerald-500' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>

              {/* Steps list */}
              <div className="space-y-1.5">
                {steps.map((step, i) => (
                  <button
                    key={step.id}
                    onClick={() => setActiveStepIndex(i)}
                    onMouseEnter={() => setHoveredStep(i)}
                    onMouseLeave={() => setHoveredStep(null)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-300 text-left ${
                      i === activeStepIndex 
                        ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                        : i < activeStepIndex
                          ? 'bg-white/5'
                          : 'bg-white/5 hover:bg-white/10'
                    }`}
                    style={{
                      transform: hoveredStep === i ? 'scale(1.02)' : 'scale(1)'
                    }}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs transition-all ${
                      i < activeStepIndex 
                        ? 'bg-emerald-500 text-white' 
                        : i === activeStepIndex
                          ? 'bg-emerald-500 text-white ring-4 ring-emerald-500/30'
                          : 'bg-white/20 text-white/60'
                    }`}>
                      {i < activeStepIndex ? '✓' : step.icon}
                    </div>
                    <span className={`text-sm font-medium ${
                      i < activeStepIndex ? 'text-white/40 line-through' : 'text-white'
                    }`}>
                      {step.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 1: Core Intelligence
// Like Apple's Camera section with big visuals
// ============================================

const intelligenceExamples = [
  {
    id: 1,
    image: '/covers/signup_cover_1.webp',
    title: 'Visual Recognition',
    description: 'Point your camera. Get instant identification.',
  },
  {
    id: 2,
    image: '/covers/signup_cover_2.webp', 
    title: 'Context Awareness',
    description: 'AI that understands your situation and goals.',
  },
  {
    id: 3,
    image: '/covers/signup_cover_0.webp',
    title: 'Expert Knowledge',
    description: 'Deep expertise across any domain you need.',
  },
];


// ============================================
// BUCKET 1: Step-by-Step Practical Guidance
// REDESIGNED: Real photo, animated steps, hover states
// ============================================

const AROverlayGuidanceSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeUseCaseIndex, setActiveUseCaseIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const useCases = [
    {
      id: 'home-repair',
      label: 'Home Repair',
      image: '/home/ar-overlay-door-hinge.webp',
      imageAlt: 'AR overlay showing door hinge repair guidance',
    },
    {
      id: 'equipment',
      label: 'Equipment Service',
      image: '/home/ar-overlay-water-heater.webp',
      imageAlt: 'AR overlay showing water heater diagnosis',
    },
    {
      id: 'nature',
      label: 'Nature Guide',
      image: '/home/ar-overlay-mushroom-id.webp',
      imageAlt: 'AR overlay showing mushroom identification',
    }
  ];

  const activeUseCase = useCases[activeUseCaseIndex];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-cycle use cases
  useEffect(() => {
    if (!isVisible || isPaused) return;
    
    const timer = setInterval(() => {
      setActiveUseCaseIndex(prev => (prev + 1) % useCases.length);
    }, 4000);
    
    return () => clearInterval(timer);
  }, [isVisible, isPaused, useCases.length]);

  const handleSelectUseCase = (index) => {
    setActiveUseCaseIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  };

  return (
    <section ref={sectionRef} className="bg-black py-16 lg:py-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div 
          className="text-left md:text-center mb-8 lg:mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            Interactive Guidance
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4">
            Never figure it out alone.
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            See it clearly with augmented reality overlays that annotate your photos in real-time. Stuck? Just point your camera and ask—visual markers show you exactly where to look and what to do.
          </p>
        </div>

        {/* Hero visual */}
        <div className="relative">
          {/* AR Image */}
          <div 
            className="relative rounded-3xl overflow-hidden shadow-2xl"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0.95)',
              transition: 'all 1s ease-out 0.2s'
            }}
          >
            <img 
              src={activeUseCase.image}
              alt={activeUseCase.imageAlt}
              className="w-full aspect-[4/3] sm:aspect-[16/9] object-cover transition-opacity duration-500"
            />
            
            {/* Simple AR Badge */}
            <div className="absolute top-4 left-4 sm:top-5 sm:left-5 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full border border-white/20 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/90 text-xs font-medium">AR Overlay</span>
            </div>
            
            {/* Gradient fade at bottom - desktop only */}
            <div className="hidden sm:block absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Use Case Selector - below image */}
          <div 
            className="flex justify-center gap-2 mt-4"
            style={{
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.8s ease-out 0.3s'
            }}
          >
            {useCases.map((uc, index) => (
              <button
                key={uc.id}
                onClick={() => handleSelectUseCase(index)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  index === activeUseCaseIndex
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                }`}
              >
                {uc.label}
              </button>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

// ============================================
// BUCKET 2: Expert Knowledge & Visual Recognition
// REDESIGNED: Multi-example carousel, interactive hover, big confidence stat
// ============================================

const ExpertKnowledgeSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeExample, setActiveExample] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [learnMoreExpanded, setLearnMoreExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Toggle learn more every 3.5 seconds
  useEffect(() => {
    if (!isVisible) return;
    const timer = setInterval(() => {
      setLearnMoreExpanded(prev => !prev);
    }, 3500);
    return () => clearInterval(timer);
  }, [isVisible]);

  const examples = [
    { 
      image: '/home/chanterelle-spotlight.webp',
      name: 'Chanterelle Mushroom',
      latin: 'Cantharellus cibarius',
      domain: 'Nature',
      tag: 'Edible',
      tagColor: 'emerald',
      spotlight: { top: '8%', left: '28%', width: '42%', height: '82%' },
      learnMore: "A prized edible mushroom found in forests worldwide. Known for its distinctive funnel shape, fruity aroma, and golden-yellow color. Best identified by its false gills — forked ridges that run down the stem rather than true blade-like gills. Commonly found near oaks, conifers, and beeches from late summer through autumn. One of the safest wild mushrooms to forage once you learn the key differences from lookalikes like the jack-o'-lantern."
    },
    { 
      image: '/home/water-heater-spotlight.webp',
      name: 'Bradford White M250',
      latin: 'Gas Water Heater',
      domain: 'Equipment',
      tag: 'Appliance',
      tagColor: 'blue',
      spotlight: { top: '2%', left: '24%', width: '28%', height: '92%' },
      learnMore: "A 50-gallon residential gas water heater common in North American homes. Key components to know: the thermostat control at the bottom sets temperature (120°F recommended), the T&P relief valve on the side prevents dangerous pressure buildup, and the anode rod inside protects against tank corrosion. Typical lifespan is 8–12 years. Annual flushing and anode rod checks can extend it significantly."
    },
    { 
      image: '/home/tomato-blight-spotlight.webp',
      name: 'Tomato Blight',
      latin: 'Phytophthora infestans',
      domain: 'Garden',
      tag: 'Plant Disease',
      tagColor: 'rose',
      spotlight: { top: '12%', left: '18%', width: '52%', height: '70%' },
      learnMore: "Late blight is the same disease that caused the Irish Potato Famine. It spreads rapidly in cool, wet conditions via airborne spores and can destroy an entire crop in days. Look for dark, water-soaked lesions on leaves and stems, often with a white fuzzy growth on the underside. Infected fruit develops firm, brown patches. Remove and destroy affected plants immediately — do not compost them. Preventive copper-based fungicides can help protect neighboring plants."
    },
  ];

  // Auto-cycle examples (pause on hover) - collapse learn more first
  useEffect(() => {
    if (!isVisible || isHovering) return;
    const timer = setInterval(() => {
      setLearnMoreExpanded(false); // Collapse first
      setTimeout(() => {
        setActiveExample(prev => (prev + 1) % examples.length);
      }, 300); // Wait for collapse animation
    }, 4000);
    return () => clearInterval(timer);
  }, [isVisible, isHovering, examples.length]);

  const current = examples[activeExample];

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Split layout: Giant stat left, visual right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left: Typography-forward headline + stats */}
          <div 
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              Multimodal Intelligence
            </p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Deep expertise.<br />
              <span className="text-white/40">On demand.</span>
            </h2>
            <p className="text-base md:text-xl font-medium text-white/60 mb-12 max-w-lg">
              Okio has deep expertise across any topic. Point your camera for instant visual recognition. Tap 'learn more' on any identification to unlock rich knowledge.
            </p>

            {/* Domain switcher badges */}
            <div 
              className="flex flex-wrap items-center gap-3"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.6s ease-out 0.4s'
              }}
            >
              {examples.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setActiveExample(i)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    i === activeExample 
                      ? 'bg-white text-black' 
                      : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {ex.domain}
                </button>
              ))}
              <span className="text-sm text-white/40 italic">
                and many more
              </span>
            </div>
          </div>
          
          {/* Right: The visual card */}
          <div 
            className="relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(40px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {/* Floating glow behind card */}
            <div className="absolute -inset-4 bg-white/5 rounded-[3rem] blur-2xl" />
            
            {/* Main card - fixed height, info slides up over image */}
            {/* Taller on mobile (3/4) to give room for learn more, shorter on desktop (4/3) */}
            <div className="relative bg-gray-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 group aspect-[3/4] sm:aspect-[4/3]">
              {/* Image - always full size */}
              <img 
                src={current.image}
                alt={current.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Spotlight box overlay - matches app style, behind info bar */}
              <div 
                className="absolute border-2 border-white rounded-2xl"
                style={{ 
                  top: current.spotlight.top,
                  left: current.spotlight.left,
                  width: current.spotlight.width,
                  height: current.spotlight.height,
                  boxShadow: '0 0 60px rgba(255,255,255,0.3), inset 0 0 30px rgba(255,255,255,0.1)',
                  animation: 'pulse 2s ease-in-out infinite',
                  transition: 'all 0.5s ease-out'
                }}
              />
              
              {/* Tag badge */}
              <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold bg-${current.tagColor}-500 text-white shadow-lg z-10`}>
                {current.tag}
              </div>

              {/* Info bar - slides up from bottom, overlays image and spotlight */}
              <div 
                className="absolute bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md p-4 sm:p-6 transition-all duration-500 ease-out z-20"
              >
                {/* Title row with learn more button */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-0.5 sm:mb-1">{current.name}</h3>
                    <p className="text-white/50 italic text-sm sm:text-base">{current.latin}</p>
                  </div>
                  <button 
                    onClick={() => setLearnMoreExpanded(!learnMoreExpanded)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0 ${
                      learnMoreExpanded 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {learnMoreExpanded ? 'Less' : 'Learn more'}
                  </button>
                </div>

                {/* Learn more content - appears below title when expanded */}
                <div 
                  className="grid transition-all duration-500 ease-out"
                  style={{
                    gridTemplateRows: learnMoreExpanded ? '1fr' : '0fr',
                  }}
                >
                  <div className="overflow-hidden">
                    <div className="pt-3 sm:pt-4 relative">
                      <p className="text-white/80 text-xs sm:text-sm leading-relaxed">
                        {current.learnMore}
                      </p>
                      {/* Fade out gradient to indicate more content */}
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

// ============================================
// BUCKET 3: Goal-Driven & Adaptive Planning  
// REDESIGNED: Before/After narrative, staggered animations
// ============================================

const AdaptivePlanningSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Stagger the plan updates
          setTimeout(() => setShowUpdates(true), 600);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const updates = [
    { type: 'new', color: 'emerald', icon: '+', label: 'Added', title: 'Document thermal refuge', reason: 'Steam pattern discovered nearby' },
    { type: 'modified', color: 'amber', icon: '↻', label: 'Updated', title: 'Extended mapping scope', reason: 'New heat sources identified' },
    { type: 'done', color: 'gray', icon: '✓', label: 'Done', title: 'Identify winter resident', reason: null },
  ];

  return (
    <section ref={sectionRef} className="bg-gradient-to-b from-[#1D1D1F] to-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Before/After transformation narrative */}
        <div className="max-w-4xl mx-auto md:text-center mb-16">
          <div 
            className="mb-8"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              Adaptive Planning
            </p>
            
            <h2 className="text-3xl sm:text-5xl lg:text-6xl max-w-2xl md:mx-auto font-bold text-white leading-[1.1]">
              Plans that adapt and guide you from A to Z.
            </h2>
          </div>
          
          <p 
            className="text-base md:text-xl font-medium text-white/60 max-w-3xl mx-auto"
            style={{
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.8s ease-out 0.3s'
            }}
          >
            Reality doesn't follow scripts—your plans shouldn't either. Dynamic goal tracking responds to what actually happens and reliably guides you to your goals, fast. From quick fixes to month-long projects—always on track.
          </p>
        </div>

        {/* Plan Update Card - animated in */}
        <div className="max-w-lg mx-auto">
          <div 
            className="bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Plan Update</h3>
                  <p className="text-white/50 text-sm">Based on your current situation</p>
                </div>
              </div>
            </div>
            
            {/* Staggered update items */}
            <div className="p-5 space-y-3">
              {updates.map((update, i) => (
                <div 
                  key={i}
                  className={`rounded-xl p-4 transition-all duration-500 ${
                    update.type === 'new' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                    update.type === 'modified' ? 'bg-amber-500/10 border border-amber-500/30' :
                    'bg-white/5 border border-white/10'
                  }`}
                  style={{
                    opacity: showUpdates ? 1 : 0,
                    transform: showUpdates ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${i * 0.15}s`
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      update.type === 'new' ? 'bg-emerald-500 text-white' :
                      update.type === 'modified' ? 'bg-amber-500 text-white' :
                      'bg-white/20 text-white/60'
                    }`}>
                      {update.icon}
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                        update.type === 'new' ? 'text-emerald-400' :
                        update.type === 'modified' ? 'text-amber-400' :
                        'text-white/40'
                      }`}>
                        {update.label}
                      </p>
                      <p className={`font-medium ${update.type === 'done' ? 'text-white/50 line-through' : 'text-white'}`}>
                        {update.title}
                      </p>
                      {update.reason && (
                        <p className="text-white/40 text-sm mt-1">{update.reason}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

// ============================================
// BUCKET 4: Contextual Awareness / Situational Intelligence
// REDESIGNED: Interactive selections, visual feedback, proactive framing
// ============================================

const ContextualAwarenessSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedTools, setSelectedTools] = useState(['WD-40']);
  const [selectedLevel, setSelectedLevel] = useState(1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const tools = ['Screwdriver', 'WD-40', 'Hammer', 'Pliers', 'Wrench'];
  const levels = ['Beginner', 'Intermediate', 'Expert'];

  const toggleTool = (tool) => {
    setSelectedTools(prev => 
      prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool]
    );
  };

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Split layout: Message left, Card right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left: The proactive agent narrative */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              Situational Intelligence
            </p>
            
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Knows your context.<br />
              <span className="text-white/40">Asks when needed.</span>
            </h2>
            
            <p className="text-base md:text-xl font-medium text-white/60 mb-8 max-w-md">
              Okio proactively gathers context—your tools, skills, constraints—to give guidance that precisely fits your situation.
            </p>

            {/* Context signals */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: '📍', label: 'Your location', active: true },
                { icon: '🛠', label: `${selectedTools.length} tools`, active: selectedTools.length > 0 },
                { icon: '📊', label: levels[selectedLevel], active: true },
                { icon: '⏱', label: 'Time limit', active: false },
              ].map((signal, i) => (
                <div 
                  key={i}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
                    signal.active 
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                      : 'bg-white/5 text-white/40 border border-white/10'
                  }`}
                >
                  <span>{signal.icon}</span>
                  <span>{signal.label}</span>
                  {signal.active && <span className="text-amber-500">✓</span>}
                </div>
              ))}
            </div>
          </div>
          
          {/* Right: Background image with form card */}
          <div
            className="relative"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(40px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {/* Workshop background image - clean, no UI baked in */}
            <div className="rounded-3xl overflow-hidden">
              <img 
                src="/home/workshop-background.jpg"
                alt="Organized workshop with tools"
                className="w-full aspect-[16/10] object-cover"
              />
            </div>
              
            {/* Interactive form card - below image on mobile, floating overlay on desktop */}
            <div className="relative mt-4 lg:mt-0 lg:absolute lg:top-1/2 lg:right-8 lg:-translate-y-1/2 w-full lg:w-80">
                <div className="bg-black/80 backdrop-blur-xl rounded-2xl border border-emerald-500/30 overflow-hidden shadow-2xl"
                  style={{ boxShadow: '0 0 60px rgba(16, 185, 129, 0.2)' }}
                >
                  {/* Form header */}
                  <div className="px-5 py-4 border-b border-white/10">
                    <h3 className="text-white font-semibold">Tell me about your setup</h3>
                  </div>
                  
                  {/* Interactive form */}
                  <div className="p-5 space-y-4">
                    {/* Tools - clickable pills */}
                    <div>
                      <label className="text-white/60 text-xs block mb-2">What tools do you have available?</label>
                      <div className="flex flex-wrap gap-2">
                        {tools.slice(0, 3).map((tool) => (
                          <button 
                            key={tool}
                            onClick={() => toggleTool(tool)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                              selectedTools.includes(tool)
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {selectedTools.includes(tool) ? '✓ ' : ''}{tool}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Experience level */}
                    <div>
                      <label className="text-white/60 text-xs block mb-2">Your experience level?</label>
                      <div className="flex gap-2">
                        {levels.map((level, i) => (
                          <button 
                            key={level}
                            onClick={() => setSelectedLevel(i)}
                            className={`flex items-center gap-1.5 text-xs transition-all ${
                              i === selectedLevel ? 'text-emerald-400' : 'text-white/50'
                            }`}
                          >
                            <div className={`w-3 h-3 rounded-full border ${
                              i === selectedLevel 
                                ? 'border-emerald-400 bg-emerald-400' 
                                : 'border-white/30'
                            }`} />
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Continue button */}
                  <div className="px-5 pb-5">
                    <button className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors">
                      Continue
                    </button>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// BUCKET 5: Location / Geo Intelligence
// REDESIGNED: Full-bleed map visual, floating location cards
// ============================================

const LocationIntelligenceSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredPin, setHoveredPin] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Procedural pins with positions
  const pins = [
    { id: 1, name: 'Chanterelle spot', distance: '200m', type: 'Mushroom habitat', x: '25%', y: '40%' },
    { id: 2, name: 'Oak Grove', distance: '400m', type: 'Primary ecosystem', x: '50%', y: '55%' },
    { id: 3, name: 'Stream crossing', distance: '800m', type: 'Water source', x: '72%', y: '35%' },
  ];

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Header */}
        <div 
          className="text-center mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-cyan-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            Location Intelligence
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Guidance that knows<br />
            <span className="text-cyan-400">where you are.</span>
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            Discover relevant locations, get directions, understand your environment—
            guidance that knows geography.
          </p>
        </div>

        {/* Map card with hybrid approach */}
        <div 
          className="relative max-w-4xl mx-auto"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.8s ease-out 0.2s'
          }}
        >
          <div className="bg-gray-900 rounded-3xl border border-gray-800 overflow-hidden shadow-2xl">
            {/* Map area - satellite background with procedural pins */}
            <div className="relative aspect-[21/9] overflow-hidden">
              {/* Satellite map background */}
              <img 
                src="/home/satellite-map-forest.jpg"
                alt="Satellite view of forest area"
                className="absolute inset-0 w-full h-full object-cover"
              />
              
              {/* Dashed route line connecting pins */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
                <path 
                  d="M 25% 40% L 50% 55% L 72% 35%"
                  stroke="rgba(6, 182, 212, 0.6)"
                  strokeWidth="3"
                  strokeDasharray="8 6"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
              
              {/* Current location indicator */}
              <div 
                className="absolute"
                style={{ left: '18%', top: '55%', zIndex: 10 }}
              >
                <div className="relative">
                  <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
                  <div className="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-50" />
                </div>
              </div>
              
              {/* Procedural pins */}
              {pins.map((pin, i) => (
                <div 
                  key={pin.id}
                  className="absolute cursor-pointer group"
                  style={{ 
                    left: pin.x, 
                    top: pin.y, 
                    zIndex: hoveredPin === pin.id ? 20 : 10,
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1)' : 'scale(0)',
                    transition: `all 0.5s ease-out ${0.3 + i * 0.1}s`
                  }}
                  onMouseEnter={() => setHoveredPin(pin.id)}
                  onMouseLeave={() => setHoveredPin(null)}
                >
                  {/* Pin marker */}
                  <div className={`
                    w-10 h-10 -ml-5 -mt-5 rounded-full flex items-center justify-center 
                    transition-all duration-300 shadow-lg
                    ${hoveredPin === pin.id 
                      ? 'bg-cyan-400 scale-125 shadow-cyan-500/50' 
                      : 'bg-cyan-500 hover:bg-cyan-400'}
                  `}>
                    <span className="text-white font-bold text-sm">{pin.id}</span>
                  </div>
                  
                  {/* Tooltip on hover */}
                  <div className={`
                    absolute left-8 top-1/2 -translate-y-1/2 
                    bg-black/90 backdrop-blur-xl rounded-xl px-4 py-3 
                    whitespace-nowrap border border-white/10
                    transition-all duration-200
                    ${hoveredPin === pin.id ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'}
                  `}>
                    <p className="text-white font-medium">{pin.name}</p>
                    <p className="text-white/50 text-sm">{pin.distance} • {pin.type}</p>
                  </div>
                </div>
              ))}
              
              {/* Badge overlay */}
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2" style={{ zIndex: 15 }}>
                <p className="text-white text-sm font-medium">3 locations found nearby</p>
              </div>
              
              {/* Bottom gradient */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-gray-900 to-transparent" style={{ zIndex: 5 }} />
            </div>
            
            {/* Location list */}
            <div className="p-6 space-y-3">
              {pins.map((pin, i) => (
                <div 
                  key={pin.id} 
                  className={`
                    flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all
                    ${hoveredPin === pin.id ? 'bg-gray-800 scale-[1.02]' : 'bg-gray-800/50 hover:bg-gray-800'}
                  `}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${0.4 + i * 0.1}s`
                  }}
                  onMouseEnter={() => setHoveredPin(pin.id)}
                  onMouseLeave={() => setHoveredPin(null)}
                >
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-colors
                    ${hoveredPin === pin.id ? 'bg-cyan-500' : 'bg-cyan-500/20'}
                  `}>
                    <span className={`font-bold ${hoveredPin === pin.id ? 'text-white' : 'text-cyan-400'}`}>{pin.id}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{pin.name}</p>
                    <p className="text-white/50 text-sm">{pin.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/70 text-sm">{pin.distance}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Observation Logging Sub-section
// Narrow band below Location Intelligence
// ============================================

const ObservationLoggingSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Photo pins with thumbnail images
  const photoPins = [
    { x: '20%', y: '35%', img: '/home/photo1.jpeg' },
    { x: '45%', y: '55%', img: '/home/photo2.jpeg' },
    { x: '70%', y: '30%', img: '/home/photo4.jpeg' },
    { x: '55%', y: '70%', img: '/home/photo3.jpeg' },
  ];

  return (
    <section ref={sectionRef} className="bg-black py-16 lg:py-20 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6">
        <div 
          className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out'
          }}
        >
          {/* Map with photo pins - larger, with satellite background */}
          <div className="relative w-full lg:w-[480px] aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 flex-shrink-0 shadow-xl">
            {/* Satellite map background */}
            <img 
              src="/home/satellite-map-forest.jpg"
              alt="Map showing your observations"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            
            {/* Slight dark overlay for pin visibility */}
            <div className="absolute inset-0 bg-black/20" />
            
            {/* Photo pins */}
            {photoPins.map((pin, i) => (
              <div 
                key={i}
                className="absolute"
                style={{ 
                  left: pin.x, 
                  top: pin.y,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'scale(1)' : 'scale(0)',
                  transition: `all 0.5s ease-out ${0.3 + i * 0.1}s`
                }}
              >
                {/* Photo thumbnail pin */}
                <div className="relative -ml-5 -mt-5 group cursor-pointer">
                  <div className="w-10 h-10 rounded-lg overflow-hidden border-2 border-white shadow-lg transition-transform group-hover:scale-110">
                    <img 
                      src={pin.img} 
                      alt="Observation" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Pin pointer */}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 shadow" />
                </div>
              </div>
            ))}
            
            {/* Current location */}
            <div 
              className="absolute"
              style={{ left: '30%', top: '50%' }}
            >
              <div className="relative -ml-2 -mt-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
                <div className="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-40" />
              </div>
            </div>
          </div>
          
          {/* Text content */}
          <div className="w-full lg:w-auto text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Every photo is geotagged.
            </h3>
            <p className="text-white/50 text-lg mb-6 max-w-md mx-auto">
              Your observations build a personal map of discoveries. 
              In public projects, they join a collective map—helping others find what you found.
            </p>
            
            {/* Shared discoveries hint */}
            <div 
              className="inline-flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl border border-white/10"
              style={{
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.6s ease-out 0.5s'
              }}
            >
              <div className="flex -space-x-2">
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-gray-800">
                  <img src="/home/photo1.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-gray-800">
                  <img src="/home/photo2.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-gray-800">
                  <img src="/home/photo4.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
              </div>
              <span className="text-white/50 text-sm">147 shared discoveries nearby</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// BUCKET 6: Long-term Memory & Analysis
// REDESIGNED: BIG stats as hero, transformation narrative
// ============================================

const LongTermMemorySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedSessions, setAnimatedSessions] = useState(0);
  const [animatedSpecies, setAnimatedSpecies] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Animate numbers
  useEffect(() => {
    if (!isVisible) return;
    const sessionTimer = setInterval(() => {
      setAnimatedSessions(prev => Math.min(prev + 1, 47));
    }, 30);
    const speciesTimer = setInterval(() => {
      setAnimatedSpecies(prev => Math.min(prev + 1, 23));
    }, 60);
    return () => {
      clearInterval(sessionTimer);
      clearInterval(speciesTimer);
    };
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="bg-gradient-to-b from-[#1D1D1F] to-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Hero stats - THE visual anchor */}
        <div 
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s ease-out'
          }}
        >

          <div className="text-center mb-16">
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              Long-term Memory
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Remembers everything.<br />
              <span className="text-white/40">Analyzes over time.</span>
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              50 sessions over 5 months? Ask for an analysis. The agent remembers 
              every observation, every discovery, every pattern.
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-end">
            <div className="max-w-lg bg-gray-700 rounded-2xl rounded-tr-sm px-5 py-4">
              <p className="text-white">
                Give me an analysis of the overall state of this forest environment 
                based on all my observations from the past 5 months.
              </p>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-2xl bg-gray-900 border border-gray-800 rounded-2xl rounded-tl-sm px-6 py-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center">
                  <span className="text-rose-400 text-sm">🧠</span>
                </div>
                <span className="text-rose-400 text-sm font-medium">Analysis from 47 sessions</span>
              </div>
              <div className="space-y-4 text-white/80">
                <p><strong className="text-white">Overall Health:</strong> The forest shows strong biodiversity with 23 unique species documented. Fungal activity peaked in October, correlating with your rainfall observations.</p>
                <p><strong className="text-white">Key Trends:</strong> Cardinal sightings increased 40% near the thermal refuge area you discovered. This suggests successful habitat adaptation.</p>
                <p><strong className="text-white">Recommendation:</strong> Continue monitoring the oak grove—early signs suggest chanterelle conditions may be ideal next season.</p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-800 flex gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">47</p>
                  <p className="text-white/50 text-xs">Sessions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">23</p>
                  <p className="text-white/50 text-xs">Species</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">5</p>
                  <p className="text-white/50 text-xs">Months</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 4: Collaboration - Horizontal Cards
// Like Apple's feature card carousel
// ============================================

const collaborationFeatures = [
  {
    id: 1,
    title: 'Real-time sync',
    description: 'Work together on shared objectives with live updates.',
    image: '/home/collab-realtime-sync.webp',
  },
  {
    id: 2,
    title: 'Live chat',
    description: 'Communicate while exploring. Share discoveries instantly.',
    image: '/home/collab-live-chat.webp',
  },
  {
    id: 3,
    title: 'Community',
    description: 'Public catalog of projects and discoveries. Ranked by users reviews.',
    image: '/home/collab-community.webp',
  },
];

const CollaborationSection = () => {
  return (
    <section className="bg-black py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            Collaboration
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1]">
            Better together.
          </h2>
        </div>

        {/* Responsive grid cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {collaborationFeatures.map((feature) => (
            <div key={feature.id}>
              <div className="rounded-3xl overflow-hidden h-[400px] sm:h-[450px] relative group">
                {/* Image */}
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Text at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// ============================================
// SECTION 5: Platform / B2B
// Stats-focused like Apple's comparison section
// ============================================

const PlatformSection = () => {
  const outcomes = [
    {
      image: '/home/platform-reduce-support.webp',
      title: 'Reduce Support Burden',
      description: 'Self-service guidance reduces tickets and calls'
    },
    {
      image: '/home/platform-accelerate-onboarding.webp',
      title: 'Accelerate Onboarding',
      description: 'New hires productive faster with guided learning'
    },
    {
      image: '/home/platform-enhance-experiences.webp',
      title: 'Enhance Experiences',
      description: 'Transform customer and visitor engagement'
    },
    {
      image: '/home/platform-capture-knowledge.webp',
      title: 'Capture Expert Knowledge',
      description: 'Preserve institutional expertise and make it available to the team'
    }
  ];

  const features = [
    { icon: '🏷️', label: 'White-label deployment', description: 'Your brand, your app' },
    { icon: '🧠', label: 'Custom knowledge grounding', description: 'Train on your domain' },
    { icon: '📊', label: 'Analytics & insights', description: 'See what users need' },
    { icon: '🔒', label: 'Enterprise security', description: 'SSO, audit logs, compliance' }
  ];

  return (
    <section className="bg-[#0a0a0a] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4 uppercase">
            For Business
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Built for your business.<br />
            <span className="text-white/40">Ready to deploy.</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
          One platform, infinite applications. From consumer apps to enterprise tools. Deploy Okio as your own product—customized for your domain, branded for your customers.
          </p>
        </div>

        {/* Outcomes grid - image on top, text below */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {outcomes.map((outcome, i) => (
            <div 
              key={i}
              className="group overflow-hidden rounded-2xl"
            >
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={outcome.image}
                  alt={outcome.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              {/* Text on black */}
              <div className="bg-black p-5">
                <h3 className="text-lg font-bold text-white mb-1">{outcome.title}</h3>
                <p className="text-white/50 text-sm">{outcome.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Platform features - with icons */}
        <div className="border-t border-white/10 pt-12">
          <p className="text-center text-white/40 text-sm uppercase tracking-wider mb-8">Platform Features</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl mb-3">{feature.icon}</div>
                <p className="text-white font-medium mb-1">{feature.label}</p>
                <p className="text-white/40 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 6: Take a Closer Look
// Interactive feature tabs with changing visual
// Like MacBook Pro / Apple Watch "Take a closer look"
// ============================================

const closerLookFeatures = [
  { 
    id: 'recognition',
    label: 'Recognition',
    icon: '👁️',
    headline: 'See it. Know it.',
    description: 'Point your camera at any plant, bird, mushroom, or object. Get instant identification with confidence scores and detailed information.',
  },
  { 
    id: 'context',
    label: 'Context',
    icon: '📍',
    headline: 'Knows where you are.',
    description: 'Location-aware intelligence factors in your environment, season, and local conditions to provide relevant guidance.',
  },
  { 
    id: 'guidance',
    label: 'Guidance',
    icon: '🧭',
    headline: 'Step by step.',
    description: 'Complex tasks broken into clear actions. Interactive cards guide your next move with visual instructions.',
  },
  { 
    id: 'memory',
    label: 'Memory',
    icon: '🧠',
    headline: 'Remembers everything.',
    description: 'Your discoveries, progress, and preferences are remembered. Pick up exactly where you left off.',
  },
  { 
    id: 'sharing',
    label: 'Sharing',
    icon: '📤',
    headline: 'Share discoveries.',
    description: 'Export your findings, share with teammates, or contribute to the community knowledge base.',
  },
];

// ============================================
// SECTION 7: Lifestyle Hero
// Full-bleed lifestyle photo with text overlay
// Like Apple Watch "Know your body by heart"
// ============================================

const LifestyleHeroSection = () => {
  return (
    <section className="relative min-h-[80vh] flex items-center">
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img 
          src="/covers/signup_cover_1.webp"
          alt=""
          className="w-full h-full object-cover"
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-24">
        <div className="max-w-2xl">
          {/* Category */}
          <p className="text-blue-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            Discovery
          </p>
          
          {/* Headline */}
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Learn by doing.<br />
            Guided every step.
          </h2>
          
          {/* Description */}
          <p className="text-base md:text-xl font-medium text-white/60 mb-8">
            The more you explore, the more you understand. Okio transforms 
            curiosity into knowledge through hands-on, guided experiences 
            that make learning feel like adventure.
          </p>

          {/* Feature highlights */}
          <div className="flex flex-wrap gap-4">
            <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
              Personalized learning paths
            </span>
            <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
              Progress tracking
            </span>
            <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
              Achievement badges
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 8: Feature Phone Gallery
// Horizontal scroll of phone screenshots
// Like Apple Watch's health features gallery
// ============================================

const phoneFeatures = [
  {
    id: 1,
    title: 'Ask anything',
    description: 'Natural conversation with an AI that truly understands context.',
    screen: 'chat',
  },
  {
    id: 2,
    title: 'Visual recognition',
    description: 'Instant identification from your camera.',
    screen: 'camera',
  },
  {
    id: 3,
    title: 'Action cards',
    description: 'Interactive guidance for your next step.',
    screen: 'action',
  },
  {
    id: 4,
    title: 'Progress tracking',
    description: 'See how far you\'ve come on your journey.',
    screen: 'progress',
  },
];

const PhoneGallerySection = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="bg-[#f5f5f7] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="px-6 mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            More features to explore.
          </h2>
          <div className="flex items-center justify-between">
            <p className="text-xl text-gray-500 max-w-2xl">
              Every interaction is designed to feel intuitive and helpful.
            </p>
            {/* Navigation arrows */}
            <div className="hidden sm:flex gap-2">
              <button 
                onClick={() => scroll('left')}
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button 
                onClick={() => scroll('right')}
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Phone carousel */}
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto px-6 pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {phoneFeatures.map((feature) => (
            <div 
              key={feature.id}
              className="flex-shrink-0 snap-start"
            >
              <div className="bg-white rounded-3xl p-6 shadow-lg w-[280px]">
                {/* Phone mockup */}
                <div className="relative w-full aspect-[9/16] bg-gray-900 rounded-[2rem] overflow-hidden mb-6">
                  <PhoneScreen type={feature.screen} />
                </div>
                {/* Feature info */}
                <h3 className="text-lg font-bold text-gray-900 mb-1">{feature.title}</h3>
                <p className="text-gray-500 text-sm">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

const PhoneScreen = ({ type }) => {
  const baseClasses = "absolute inset-0 p-4 flex flex-col";
  
  switch (type) {
    case 'chat':
      return (
        <div className={baseClasses}>
          <div className="flex-1 space-y-3 pt-8">
            <div className="flex justify-end">
              <div className="bg-emerald-600 rounded-2xl rounded-br-sm px-3 py-2 max-w-[80%]">
                <p className="text-xs text-white">What mushroom is this?</p>
              </div>
            </div>
            <div className="flex justify-start">
              <div className="bg-gray-700 rounded-2xl rounded-bl-sm px-3 py-2 max-w-[80%]">
                <p className="text-xs text-gray-200">This appears to be a Chanterelle (Cantharellus cibarius). Notice the distinctive wavy cap and false gills...</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-2 flex items-center gap-2">
            <div className="flex-1 bg-gray-700 rounded-lg px-3 py-2">
              <span className="text-gray-400 text-xs">Ask a question...</span>
            </div>
          </div>
        </div>
      );
    case 'camera':
      return (
        <div className={baseClasses + " justify-center items-center bg-gradient-to-b from-green-900 to-green-950"}>
          <div className="w-32 h-32 border-2 border-white/50 rounded-xl mb-4" />
          <div className="bg-black/50 backdrop-blur-sm rounded-xl px-4 py-2">
            <p className="text-white text-sm font-bold">Scanning...</p>
          </div>
        </div>
      );
    case 'action':
      return (
        <div className={baseClasses + " justify-end pb-8"}>
          <div className="bg-emerald-900/50 border border-emerald-500/30 rounded-xl p-4">
            <p className="text-emerald-400 text-xs font-bold mb-1">Next Step</p>
            <p className="text-white text-sm mb-3">Gently brush away debris from the cap to examine the gills.</p>
            <button className="w-full py-2 bg-emerald-500 rounded-lg text-white text-sm font-bold">
              Done
            </button>
          </div>
        </div>
      );
    case 'progress':
      return (
        <div className={baseClasses + " pt-12"}>
          <div className="text-center mb-6">
            <div className="text-4xl font-bold text-emerald-400">73%</div>
            <p className="text-gray-400 text-xs">Quest Progress</p>
          </div>
          <div className="space-y-3">
            {['Identify 5 species', 'Take 3 photos', 'Complete quiz'].map((task, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${i < 2 ? 'bg-emerald-500' : 'bg-gray-600'}`} />
                <span className={`text-xs ${i < 2 ? 'text-gray-300' : 'text-gray-500'}`}>{task}</span>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

// ============================================
// SECTION 9: Big Stats Section
// Scroll-triggered stats with animation
// Like MacBook Pro "Up to 6x faster"
// ============================================

const BigStatsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: '10x', label: 'faster learning', color: 'text-emerald-400' },
    { value: '24/7', label: 'expert availability', color: 'text-blue-400' },
    { value: '∞', label: 'possible domains', color: 'text-purple-400' },
  ];

  return (
    <section ref={sectionRef} className="bg-black py-32 lg:py-48">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Big statement */}
        <div className="text-center mb-24">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05]">
            One assistant.<br />
            <span className="text-white/40">Second to none.</span>
          </h2>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 lg:gap-16">
          {stats.map((stat, index) => (
            <div 
              key={stat.label}
              className="text-center"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: `all 0.8s ease ${index * 0.15}s`
              }}
            >
              <div className={`text-6xl sm:text-7xl lg:text-8xl font-bold ${stat.color} mb-4`}>
                {stat.value}
              </div>
              <div className="text-xl text-white/60">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 10: Three Column Features
// Icon + title + description cards
// Like Apple Watch health features
// ============================================

const threeColumnFeatures = [
  {
    icon: '🎯',
    title: 'Objective tracking',
    description: 'Set goals, track progress, and celebrate achievements as you learn and explore.',
  },
  {
    icon: '🗺️',
    title: 'Location intelligence',
    description: 'GPS tracking, map integration with discovery pins, and reverse geocoding for nearby spots.',
  },
  {
    icon: '🏆',
    title: 'Achievements',
    description: 'Earn badges, unlock levels, and build your expertise profile over time.',
  },
];

// ============================================
// SECTION 11: AR Overlay Section (NEW)
// Shows the photo → AI visual guidance feature
// Cloned/remixed approach to compare
// ============================================

const AROverlaySection = () => {
  const [showOverlay, setShowOverlay] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Auto-toggle overlay after a delay when section becomes visible
          setTimeout(() => setShowOverlay(true), 800);
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-blue-400 text-sm font-bold uppercase tracking-wider mb-4">
            Visual Guidance
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Show it. Fix it.<br />
            <span className="text-white/40">See exactly what to do.</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Upload a photo of your problem. Get AI-generated visual overlays 
            that show you exactly where to look and what to do.
          </p>
        </div>

        {/* Interactive demo */}
        <div className="max-w-4xl mx-auto">
          {/* Phone frame with AR demo */}
          <div className="relative mx-auto w-[320px] sm:w-[380px]">
            <div className="bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
              <div className="bg-[#1a1a18] rounded-[2.5rem] overflow-hidden">
                {/* Screen content */}
                <div className="aspect-[9/16] relative">
                  {/* Base image - a door hinge */}
                  <div className="absolute inset-0 bg-gradient-to-b from-amber-900/30 to-amber-950/50 flex items-center justify-center">
                    {/* Simulated door hinge photo */}
                    <div className="relative w-full h-full p-8">
                      {/* Door frame representation */}
                      <div className="absolute left-8 top-16 bottom-16 w-4 bg-amber-800/60 rounded" />
                      <div className="absolute left-12 top-20 bottom-20 w-32 bg-amber-700/40 rounded-r-lg" />
                      
                      {/* Hinge representations */}
                      <div className="absolute left-6 top-24 w-8 h-12 bg-gray-600 rounded" />
                      <div className="absolute left-6 top-1/2 -translate-y-1/2 w-8 h-12 bg-gray-600 rounded" />
                      <div className="absolute left-6 bottom-24 w-8 h-12 bg-gray-600 rounded" />

                      {/* AR Overlays - appear when toggled */}
                      {showOverlay && (
                        <>
                          {/* Highlight circle on middle hinge */}
                          <div 
                            className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-16 border-2 border-emerald-400 rounded-lg animate-pulse"
                            style={{ boxShadow: '0 0 20px rgba(52, 211, 153, 0.5)' }}
                          />
                          
                          {/* Arrow pointing to hinge */}
                          <div className="absolute left-20 top-1/2 -translate-y-1/2 flex items-center gap-2 animate-fadeIn">
                            <div className="w-8 h-0.5 bg-emerald-400" />
                            <div className="w-0 h-0 border-t-4 border-b-4 border-l-8 border-transparent border-l-emerald-400" />
                          </div>
                          
                          {/* Instruction card */}
                          <div 
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-40 bg-black/80 backdrop-blur-sm border border-emerald-500/30 rounded-xl p-3 animate-fadeIn"
                            style={{ animationDelay: '200ms' }}
                          >
                            <p className="text-emerald-400 text-xs font-bold mb-1">Step 1</p>
                            <p className="text-white text-sm">Apply 2-3 drops of lubricant to the hinge pin here</p>
                          </div>
                          
                          {/* Top annotation */}
                          <div 
                            className="absolute left-4 top-20 w-14 h-16 border border-white/30 rounded-lg animate-fadeIn"
                            style={{ animationDelay: '400ms' }}
                          />
                          <div 
                            className="absolute left-20 top-24 text-white/50 text-xs animate-fadeIn"
                            style={{ animationDelay: '400ms' }}
                          >
                            Upper hinge ✓
                          </div>
                          
                          {/* Bottom annotation */}
                          <div 
                            className="absolute left-4 bottom-20 w-14 h-16 border border-white/30 rounded-lg animate-fadeIn"
                            style={{ animationDelay: '600ms' }}
                          />
                          <div 
                            className="absolute left-20 bottom-24 text-white/50 text-xs animate-fadeIn"
                            style={{ animationDelay: '600ms' }}
                          >
                            Lower hinge (next)
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Bottom chat input area */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                      <p className="text-white/70 text-sm">
                        {showOverlay 
                          ? "I've highlighted the squeaky hinge. Start with the middle one." 
                          : "My door is squeaking. Here's a photo of the hinges..."}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Home indicator */}
                <div className="py-2 flex justify-center">
                  <div className="w-28 h-1 bg-white/20 rounded-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Toggle button */}
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowOverlay(!showOverlay)}
              className={`px-6 py-3 rounded-full text-sm font-bold transition-all ${
                showOverlay
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {showOverlay ? 'Showing AI Overlays' : 'Show AI Visual Guidance'}
            </button>
          </div>

          {/* Feature callouts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="text-3xl mb-3">📸</div>
              <h3 className="text-white font-bold mb-1">Upload Any Photo</h3>
              <p className="text-white/50 text-sm">Take a picture of your problem</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-white font-bold mb-1">AI Analyzes & Annotates</h3>
              <p className="text-white/50 text-sm">Visual overlays highlight what matters</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-3">✅</div>
              <h3 className="text-white font-bold mb-1">Follow Step by Step</h3>
              <p className="text-white/50 text-sm">Clear guidance right on your image</p>
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

// ============================================
// SECTION 12: Eyes Ring Section
// Inspired by Apple Watch battery ring
// Giant animated eyes in the center
// ============================================

const EyesRingSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-[#f5f5f7] py-32 lg:py-48">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Category label */}
        <p className="text-center text-gray-500 text-sm font-bold uppercase tracking-wider mb-16">
          Awareness
        </p>

        {/* Giant ring with eyes */}
        <div className="flex justify-center mb-16">
          <div 
            className="relative"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0.8)',
              transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            {/* Outer ring - animated gradient */}
            <div className="w-[280px] h-[280px] sm:w-[360px] sm:h-[360px] lg:w-[420px] lg:h-[420px] rounded-full p-3 bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-600 shadow-2xl shadow-emerald-500/30">
              {/* Inner white circle */}
              <div className="w-full h-full rounded-full bg-[#f5f5f7] flex flex-col items-center justify-center">
                {/* Giant animated eyes */}
                <div className="scale-[5] sm:scale-[6] lg:scale-[7] transform-gpu mb-4">
                  <LoadingIndicator className="!mt-0 !py-0 !px-0" />
                </div>
                
                {/* Text inside ring */}
                <div className="mt-8 text-center">
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 leading-tight">
                    Always watching.
                  </p>
                  <p className="text-lg sm:text-xl text-gray-500 mt-1">
                    Never missing a thing.
                  </p>
                </div>
              </div>
            </div>

            {/* Subtle glow effect */}
            <div className="absolute inset-0 rounded-full bg-emerald-400/20 blur-3xl -z-10 scale-110" />
          </div>
        </div>

        {/* Description paragraph */}
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-16 text-lg leading-relaxed">
          Your AI companion sees what you see, understands what you're doing, and stays 
          alert to opportunities and challenges. Context-aware intelligence that's always 
          one step ahead—so you never have to figure things out alone.
        </p>

        {/* Stats row - like Apple's "Up to 24hrs / 38hrs / 8hrs" */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="text-center">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Identifies</p>
            <p className="text-4xl sm:text-5xl font-bold text-emerald-500">1000+</p>
            <p className="text-sm text-gray-500 mt-1">species & objects</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Responds in</p>
            <p className="text-4xl sm:text-5xl font-bold text-emerald-500">&lt;2s</p>
            <p className="text-sm text-gray-500 mt-1">average response</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Available</p>
            <p className="text-4xl sm:text-5xl font-bold text-emerald-500">24/7</p>
            <p className="text-sm text-gray-500 mt-1">expert guidance</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION: Omnichannel Presence
// Shows that Okio integrates with many platforms
// ============================================

const OmnichannelSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredChannel, setHoveredChannel] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const channels = [
    {
      name: 'WhatsApp',
      color: '#25D366',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
    },
    {
      name: 'Telegram',
      color: '#26A5E4',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
          <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      ),
    },
    {
      name: 'Teams',
      color: '#6264A7',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
          <path d="M20.625 8.5h-6.25a.625.625 0 00-.625.625v6.25c0 .345.28.625.625.625h6.25c.345 0 .625-.28.625-.625v-6.25a.625.625 0 00-.625-.625zM17.5 7.5a2 2 0 100-4 2 2 0 000 4zm4.5 1a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm.5 2v4.5a2.5 2.5 0 01-2.5 2.5H20v-7h2.5zM13 3.5a3 3 0 11-6 0 3 3 0 016 0zM.5 19.5c0-3.59 2.91-6.5 6.5-6.5h6c3.59 0 6.5 2.91 6.5 6.5 0 .828-.672 1.5-1.5 1.5h-16A1.5 1.5 0 01.5 19.5z"/>
        </svg>
      ),
    },
    {
      name: 'Slack',
      color: '#E01E5A',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
          <path d="M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 01-2.523 2.521 2.527 2.527 0 01-2.52-2.521V2.522A2.527 2.527 0 0115.165 0a2.528 2.528 0 012.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 012.523 2.522A2.528 2.528 0 0115.165 24a2.527 2.527 0 01-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 01-2.52-2.523 2.526 2.526 0 012.52-2.52h6.313A2.527 2.527 0 0124 15.165a2.528 2.528 0 01-2.522 2.523h-6.313z"/>
        </svg>
      ),
    },
    {
      name: 'Email',
      color: '#EA4335',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 4L12 13 2 4" />
        </svg>
      ),
    },
    {
      name: 'Messenger',
      color: '#0084FF',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
          <path d="M.001 11.639C.001 4.949 5.241 0 12.001 0S24 4.95 24 11.639c0 6.689-5.24 11.638-12 11.638-1.21 0-2.38-.16-3.47-.46a.96.96 0 00-.64.05l-2.39 1.05a.96.96 0 01-1.35-.85l-.07-2.14a.97.97 0 00-.32-.68A11.39 11.389 0 01.002 11.64zm8.32-2.19l-3.52 5.6c-.35.53.32 1.139.82.75l3.79-2.87c.26-.2.6-.2.87 0l2.8 2.1c.84.63 2.04.4 2.6-.48l3.52-5.6c.35-.53-.32-1.13-.82-.75l-3.79 2.87c-.25.2-.6.2-.86 0l-2.8-2.1a1.8 1.8 0 00-2.61.48z"/>
        </svg>
      ),
    },
    {
      name: 'Instagram',
      color: '#E4405F',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z"/>
        </svg>
      ),
    },
    {
      name: 'SMS',
      color: '#34C759',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          <path d="M8 10h.01M12 10h.01M16 10h.01" />
        </svg>
      ),
    },
  ];

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6">

        {/* Header */}
        <div
          className="text-center mb-16 lg:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4 uppercase">
            Omnichannel
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Wherever your<br />customers are.
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl mx-auto">
            Deploy the Okio Agent across every channel your customers already use. One brain, every platform—seamless support without switching tools.
          </p>
        </div>

        {/* Channel logos grid */}
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6 max-w-4xl mx-auto"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.8s ease-out 0.2s'
          }}
        >
          {channels.map((channel, i) => {
            const isHovered = hoveredChannel === i;
            return (
              <div
                key={channel.name}
                className="relative"
                onMouseEnter={() => setHoveredChannel(i)}
                onMouseLeave={() => setHoveredChannel(null)}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                  transition: `all 0.5s ease-out ${0.25 + i * 0.06}s`
                }}
              >
                <div
                  className="flex flex-col items-center gap-3 py-6 px-4 rounded-2xl transition-all duration-300"
                  style={{
                    backgroundColor: isHovered ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.03)',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: isHovered ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                  }}
                >
                  <div
                    className="transition-all duration-300"
                    style={{
                      color: isHovered ? channel.color : 'rgba(255,255,255,0.35)',
                      filter: isHovered ? `drop-shadow(0 0 12px ${channel.color}80)` : 'none',
                    }}
                  >
                    {channel.icon}
                  </div>
                  <span
                    className="text-sm font-medium transition-colors duration-300"
                    style={{ color: isHovered ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)' }}
                  >
                    {channel.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Subtle bottom note */}
        <p
          className="text-center text-white/30 text-sm mt-10"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.8s ease-out 0.6s'
          }}
        >
          and any platform with an API
        </p>
      </div>
    </section>
  );
};

// ============================================
// BUCKET 9: Sammelbecken (Catch-all Features)
// REDESIGNED: Horizontal scroll, hover reveals, minimal text
// ============================================

const MoreFeaturesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: '🚀', title: 'Starters', description: 'Pre-built templates for common tasks' },
    { icon: '📂', title: 'Projects', description: 'Multi-session continuity' },
    { icon: '⭐', title: 'Ratings', description: 'Improve through feedback' },
    { icon: '🗄', title: 'ThingsDB', description: 'Your discoveries, organized' },
    { icon: '🎯', title: 'Prompts', description: 'Customizable guidance styles' },
    { icon: '📊', title: 'Progress', description: 'Track growth over time' },
  ];

  return (
    <section ref={sectionRef} className="bg-[#0a0a0a] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Header */}
        <div 
          className="text-left md:text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-gray-400 text-sm lg:text-lg font-bold tracking-wider mb-4">And More</p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Everything you need.<br />
            <span className="text-white/40">Nothing you don't.</span>
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            The little things that make the difference. Quick starts, session history, ratings, and a persistent knowledge base.
          </p>
        </div>

        {/* Feature cards - grid */}
        <div 
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.8s ease-out 0.3s'
          }}
        >
          {features.map((feature, i) => (
            <div 
              key={i}
              onMouseEnter={() => setHoveredFeature(i)}
              onMouseLeave={() => setHoveredFeature(null)}
              className="group"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.5s ease-out ${0.3 + i * 0.08}s`
              }}
            >
              <div 
                className={`bg-gray-900 rounded-2xl p-5 border border-gray-800 transition-all duration-300 cursor-default h-full ${
                  hoveredFeature === i ? 'border-gray-600 scale-105' : ''
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center mb-3">
                  <span className="text-xl">{feature.icon}</span>
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">{feature.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

// ============================================
// HOW IT WORKS: IKEA-style explainer
// ============================================
const HowItWorksSection = () => {
  const useCases = [
    {
      image: '/home/hero-ikea-explainer.png',
      label: 'Home Repair',
      alt: 'How Okio helps with home repair tasks'
    },
    {
      image: '/home/hero-ikea-nature.png',
      label: 'Nature Guide',
      alt: 'How Okio helps identify nature and wildlife'
    },
    {
      image: '/home/hero-ikea-equipment.webp',
      label: 'Equipment Service',
      alt: 'How Okio helps technicians diagnose equipment'
    }
  ];

  return (
    <section className="bg-black py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-8 lg:mb-12">
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4 uppercase">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1]">
            From confusion to completion.
          </h2>
        </div>
        
        {/* 3 use cases side by side */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {useCases.map((uc, i) => (
            <div key={i} className="text-center">
              <img 
                src={uc.image}
                alt={uc.alt}
                className="w-full rounded-2xl mb-4"
              />
              <p className="text-white font-semibold text-lg">{uc.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CapabilityShowcase = () => {
  return (
    <div>
      {/* SLIDE 2: Interactive Demo (Promise Section) */}
      <PromiseSection />
      
      {/* SLIDE 3: Step-by-Step Walkthrough with four-panel illustrations */}
      <GuidedWalkthroughSection />
      
      {/* SLIDE 4: AR Overlay Guidance */}
      <AROverlayGuidanceSection />
      
      {/* SLIDE 5: Expert Knowledge & Visual Recognition */}
      <ExpertKnowledgeSection />
      
      {/* SLIDE 5: Goal-Driven & Adaptive Planning */}
      <AdaptivePlanningSection />
      
      {/* SLIDE 6: Contextual Awareness / Situational Intelligence */}
      <ContextualAwarenessSection />
      
      {/* SLIDE 7: Location / Geo Intelligence */}
      <LocationIntelligenceSection />
      
      {/* Observation logging sub-section */}
      <ObservationLoggingSection />
      
      {/* SLIDE 8: Long-term Memory & Analysis */}
      <LongTermMemorySection />
      
      {/* SLIDE 9: Collaboration (Better Together) */}
      <CollaborationSection />
      
      {/* SLIDE 10: B2B / Platform */}
      <PlatformSection />
      
      {/* SLIDE 10.5: Omnichannel Presence */}
      <OmnichannelSection />
      
      {/* SLIDE 11: Sammelbecken (More Features) */}
      <MoreFeaturesSection />
      
      {/* ============================================
          LEGACY SECTIONS - KEPT FOR REFERENCE/REMIX
          Can be removed or reintegrated later
          ============================================ */}

      {/* <AROverlaySection />
      <PhoneGallerySection />
      <BigStatsSection />
      <EyesRingSection />
      <LifestyleHeroSection /> */}
      
    </div>
  );
};

export default CapabilityShowcase;
